import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import type { SystemModule, PermissionAction } from '@/types';

interface RoleGuardProps {
    children: React.ReactNode;
    requiredRole?: string | string[];
    requiredPermission?: {
        module: SystemModule;
        action: PermissionAction;
    };
}

export const RoleGuard: React.FC<RoleGuardProps> = ({ children, requiredRole, requiredPermission }) => {
    const { currentUser, hasPermission } = useAuth();

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // Super admin bypass
    if (currentUser.role === 'super_admin') {
        return <>{children}</>;
    }

    // Permission-based check (if specified)
    if (requiredPermission) {
        const hasAccess = hasPermission(requiredPermission.module, requiredPermission.action);
        if (hasAccess) {
            return <>{children}</>;
        }
    }

    // Role-based check (legacy support)
    if (requiredRole) {
        const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

        // Multi-role support
        const hasRole = roles.includes(currentUser.role) ||
            (currentUser.roleIds && currentUser.roleIds.some(r => roles.includes(r)));

        if (hasRole) {
            return <>{children}</>;
        }
    }

    // No access - redirect to dashboard
    return <Navigate to="/dashboard" replace />;
};
